import { memo, useState, useMemo, useEffect, type FunctionComponent, type MouseEventHandler } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
// Skillmore UI Components
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import GridBox from '@empathco/ui-components/src/mixins/GridBox';
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
// local imports
import { SkillWithLevel } from '../graphql/types';
import { TalentEmployeeObject, DevPlanEmployee } from '../graphql/customTypes';
import TbItemChip from '../elements/TbItemChip';
import EmployeeSimpleBand from '../elements/EmployeeSimpleBand';
// SCSS imports
import { reqSkillsTitle } from './EmployeeDetailsPopup.module.scss';

const contentSx: SxProps<Theme> = { py: 1 } as const;

type EmployeeDetailsPopupProps = {
  readonly title?: string;
  readonly employee?: TalentEmployeeObject | DevPlanEmployee;
  readonly targetSkills?: SkillWithLevel[] | null;
  readonly isOpen?: boolean;
  readonly onClose: MouseEventHandler<HTMLButtonElement>;
  readonly route?: string | null;
  readonly isMyPlan?: boolean;
}

const EmployeeDetailsPopup: FunctionComponent<EmployeeDetailsPopupProps> = ({
  title,
  employee,
  targetSkills,
  isOpen = false,
  onClose,
  route,
  isMyPlan = false
}) => {
  const skills = targetSkills || employee?.skills as SkillWithLevel[];

  const [mounted, setMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setMounted(true);
  }, [isOpen]);

  const transitionProps = useMemo(() => ({ onExited: () => {
    setMounted(false);
  } }), []);

  return mounted ? (
    <Dialog
        disableEnforceFocus
        maxWidth="lg"
        fullWidth
        scroll="body"
        open={isOpen}
        onClose={onClose}
        TransitionProps={transitionProps}
    >
      <CloseIconButton onClick={onClose}/>
      {(isMyPlan && employee) || title ? <CardTitle title={title || 'dev_plans.skills'} withDivider/> : undefined}
      <DialogContent sx={contentSx}>
        {isMyPlan || !employee ? undefined : (
          <>
            <GridBox container spacing={2} pt={3} pb={2}>
              <EmployeeSimpleBand employee={employee} route={route}/>
            </GridBox>
            <Divider/>
          </>
        )}
        {size(skills) < 1 ? (
          <InlineAlert severity="info" message="skills.no_skills_found"/>
        ) : (
          <>
            <BoxTypography pt={2.5} pb={1.5} color="text.secondary" className={reqSkillsTitle}>
              <FormattedMessage id={targetSkills ? 'hr.dev_plan.covered_skills' : 'hr.cohort.skills.required'}/>
            </BoxTypography>
            <Box display="flex" flexWrap="wrap">
              {map(skills, (skill, index) => (
                <Box key={index} pb={3} pr={3}>
                  <TbItemChip
                      item={skill}
                      satisfied={isNil(skill.is_satisfied) || skill.is_satisfied}
                      withLargeLevel
                      // TODO: onClick - navigate to relevant skill details screen
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  ) : null;
};

export default memo(EmployeeDetailsPopup);
